import { useCallback, useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, Box, IconButton, Typography,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import RestoreIcon from '@mui/icons-material/Restore'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { FancyButton } from '../../comps/FancyButtons'
import { DataTable, type DataTableColumn } from '../../comps/DataTable'
import ConfirmModal from './ConfirmModal'
import { FS_SM, FS_XS, ICON_SM } from '../../common/theme'
import {
  pmGetProjectHistory,
  pmGetProjectHistoryVersion,
  type ProjectHistoryRow,
} from '../../services/ProjectManagerService'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { formatDurationSince, parseUtcTimestamp } from '../../common/common'
import type { Project } from '../../types'

interface BidProjectHistoryModalProps {
  open: boolean
  projectsha: string | undefined
  onClose: () => void
  // Fires after the rolled-back project has been persisted, so the parent
  // can pull the project state forward into its own view.
  onRollback?: (project: Project) => void
}

const PAGE_SIZE = 20

// Modal that lists the saved historical versions of a project and lets the
// user roll back to one. List comes from /api2/pmgethistory (lightweight
// rows, no payload); a per-row "Restore" button loads the full snapshot
// via /api2/pmgethistoryversion, confirms with the user, then overwrites
// the current project + persists via pmSaveProject. All locally-modified
// questions are discarded - the confirm dialog calls this out.
export default function BidProjectHistoryModal({
  open, projectsha, onClose, onRollback,
}: BidProjectHistoryModalProps) {
  const { updateProject } = useAppActions()

  const [versions, setVersions] = useState<ProjectHistoryRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const [confirmVersion, setConfirmVersion] = useState<number | null>(null)
  const [isRestoring, setIsRestoring] = useState(false)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const fetchPage = useCallback(async (p: number) => {
    if (!projectsha) return
    setIsLoading(true)
    try {
      const res = await pmGetProjectHistory(projectsha, p, PAGE_SIZE)
      if (res?.success) {
        setVersions(res.versions ?? [])
        setTotal(res.total ?? 0)
        setPage(res.page ?? p)
      } else {
        addToast('error', res?.msg ?? 'Failed to load project history')
      }
    } catch {
      addToast('error', 'Failed to load project history')
    } finally {
      setIsLoading(false)
    }
  }, [projectsha, addToast])

  useEffect(() => {
    if (!open) return
    setPage(0)
    setVersions([])
    setTotal(0)
    fetchPage(0)
  }, [open, fetchPage])

  const handleRestore = useCallback(async () => {
    if (confirmVersion == null || !projectsha) return
    setIsRestoring(true)
    try {
      const res = await pmGetProjectHistoryVersion(projectsha, confirmVersion)
      if (!res?.success) {
        addToast('error', 'Failed to load version')
        return
      }
      // The snapshot carries the project shell + the full questions list
      // as they were at save time. Apply on top of the current project so
      // identifiers the server keys on (projectsha, orgid) are preserved
      // while every editable field gets reset to the historical value.
      // The rollback is applied to local state only - force `modified: true`
      // so the Save Bid button lights up (the historical snapshot itself
      // carries the old `modified: false` it was saved with).
      updateProject({ ...res, modified: true })
      onRollback?.(res)
      addToast('success', `Rolled back to version ${confirmVersion}. Save to commit`)
      setConfirmVersion(null)
      onClose()
    } catch {
      addToast('error', 'Failed to roll back to selected version')
    } finally {
      setIsRestoring(false)
    }
  }, [confirmVersion, projectsha, addToast, updateProject, onRollback, onClose])

  const cols: DataTableColumn<ProjectHistoryRow>[] = [
    {
      id: 'version', label: 'Version', align: 'center', width: 90, sortable: false,
      render: (v) => (
        <Typography variant="body2" sx={{ fontSize: FS_SM, fontWeight: 600 }}>
          {v.version}
        </Typography>
      ),
    },
    {
      id: 'saved', label: 'Saved', flex: 1, width: 0, align: 'center', minWidth: 140, sortable: false,
      render: (v) => (
        <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.7 }}>
          {new Date(parseUtcTimestamp(v.versionsaved)).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'age', label: 'Age', width: 80, align: 'center', sortable: false,
      render: (v) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: ICON_SM, opacity: 0.6 }} />
          <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.8 }}>
            {formatDurationSince(v.versionsaved)}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'action', label: '', width: 110, align: 'center', sortable: false, iconColumn: true,
      render: (v) => (
        <FancyButton
          testId={`bph_restore_btn_${v.version}`}
          disabled={isRestoring}
          onClick={() => setConfirmVersion(v.version)}
        >
          Roll&nbsp;back
        </FancyButton>
      ),
    },
  ]

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 'data-testid': 'bph_dlg', sx: { height: '70vh', display: 'flex', flexDirection: 'column' } } as any}
      >
        <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon sx={{ fontSize: ICON_SM }} />
          <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem', flex: 1 }}>
            Project History
          </Typography>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <DataTable
            rows={versions}
            cols={cols}
            rowKey={(v) => v.version}
            rowTestId="bph_row"
            rowDataAttrs={(v) => ({ 'data-version': v.version })}
            loading={isLoading}
            testIdPrefix="bph_table"
            emptyContent={
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: FS_SM, opacity: 0.6 }}>
                  No saved versions yet. Save the bid to create one.
                </Typography>
              </Box>
            }
          />
        </DialogContent>

        {total > PAGE_SIZE && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, px: 1.5, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.7, mr: 1 }}>
              Page {page + 1} of {totalPages}
            </Typography>
            <IconButton size="small" data-testid="bph_prev_btn" disabled={page <= 0 || isLoading} onClick={() => fetchPage(page - 1)}>
              <ChevronLeftIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
            <IconButton size="small" data-testid="bph_next_btn" disabled={page + 1 >= totalPages || isLoading} onClick={() => fetchPage(page + 1)}>
              <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Box>
        )}
      </Dialog>

      <ConfirmModal
        open={confirmVersion != null}
        title="Roll back to previous version?"
        body={
          confirmVersion != null
            ? `This will replace the current questions with version ${confirmVersion} and discard every unsaved or modified question in this bid. The rollback is local until you save the bid.`
            : ''
        }
        okLabel={isRestoring ? 'Rolling back…' : 'Roll back'}
        testIdPrefix="bph_confirm"
        onConfirm={handleRestore}
        onClose={() => { if (!isRestoring) setConfirmVersion(null) }}
      />
    </>
  )
}
